import { useTranslation } from "react-i18next";

import { formatDateAt } from "simplydo/core/dates";
import type { Invitation, UseInvitationOptions } from "simplydo/interfaces";
import { useInvitation, useMe } from "simplydo/react/hooks";
import { Button } from "simplydo/react/ui/button";
import { H4 } from "simplydo/react/ui/header";
import { Message } from "simplydo/react/ui/message";

import util from "utils/utils";

import { HomeRowActions, HomeRowContent } from "components/home/HomeUI";
import InviteeChip from "components/lib/Invitations/InviteCard/InviteeChip";
import InviteLink from "components/lib/Invitations/inviteLink";

const invitationHookOptions: UseInvitationOptions = {
  onAccept: ({ title, message, confirm, cancel }) => {
    util.confirm(title, message).then(confirm).catch(cancel);
  },
};

const InvitationBlockRow = ({ invitation }: { invitation: Invitation }) => {
  const vm = useInvitation(invitation, invitationHookOptions);
  const me = useMe();
  const { t } = useTranslation();
  const { createdByCurrentUser } = invitation;
  const { text } = vm;

  const dismissHandler = createdByCurrentUser
    ? vm.removeInvite
    : vm.rejectInvite;

  const showAccept =
    vm.showAccept ?? (!!me && !!invitation.canAccept && !invitation.acceptedAt);
  const showDismiss =
    vm.showDismiss ??
    (!!me && !!invitation.canRemove && !invitation.acceptedAt);

  return (
    <>
      <HomeRowContent>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems: "flex-start",
          }}
        >
          {vm.link ? (
            <InviteLink to={vm.link}>
              <H4>{text.title}</H4>
            </InviteLink>
          ) : (
            <H4>{text.title}</H4>
          )}
          <div>
            <InviteeChip
              invitation={invitation}
              youInvited={text.youInvited}
              hasInvitedYou={text.hasInvitedYou}
            />
          </div>
          <span style={{ fontSize: 12, color: "#777" }}>
            {formatDateAt(invitation.acceptedAt ?? invitation.createdAt)}
          </span>
        </div>{" "}
        {invitation.acceptedAt && vm.link && text.goTo ? (
          <InviteLink to={vm.link}>
            <Button variant="soft" color="primary">
              {text.goTo}
            </Button>
          </InviteLink>
        ) : null}
      </HomeRowContent>
      <HomeRowActions>
        {!invitation.acceptedAt && (
          <>
            {showAccept && vm.acceptInvite ? (
              <Button
                variant="soft"
                size="small"
                color="primary"
                onClick={vm.acceptInvite}
              >
                {t("invitations.accept")}
              </Button>
            ) : null}
            {vm.isInvalid ? (
              <Message color="red" style={{ width: "100%" }}>
                {text.invalid}
              </Message>
            ) : null}
            {showDismiss && dismissHandler ? (
              <Button size="small" variant="outline" onClick={dismissHandler}>
                {text.dismiss}
              </Button>
            ) : null}
          </>
        )}
      </HomeRowActions>
    </>
  );
};

export default InvitationBlockRow;
